import { Key, useState } from 'react'
import { navigate } from 'gatsby'
import clsx from 'clsx'
import moment from 'moment'
import {
  Alert,
  Button,
  Card,
  Col,
  DateRangePicker,
  Error,
  ErrorAlert,
  Form,
  Info,
  List,
  Modal,
  Row,
  SEO,
  showMessage,
  TimeRangePicker,
  Title,
} from 'components/common'
import {
  BookingDetail,
  BookingInfoCard,
  BookingUsageScheduleDetail,
  BulkTable,
  CustomerSearchCard,
  CustomVariantAvailable,
  FilterFormElement,
  SearchConditionsCard,
} from 'components/bookings'
import {
  Customer,
  CustomerConditionType,
  CustomerQueryInput,
  CustomerQueryType,
  OrderCreateBatchInput,
  useCustomersLazyQuery,
  useOrderCreateBatchMutation,
  useProductWholeBookingLazyQuery,
} from 'lib/graphql/storeadmin'
import { Anchor } from '../status'
import { dateRegexFormat } from 'lib/constants'
import { bookingsErrorHandling } from 'lib/hooks'
import {
  getAnyMatchDates,
  getGivenDayMatch,
  getGroupOfDaysMatch,
  getWeekInMonthAndGroupOfDaysMatch,
} from 'utils/custom-functions'
import { routes } from 'config'

export interface DatetimeSearchForm {
  startDateToEndDate: [moment.Moment, moment.Moment]
  timezone: [moment.Moment, moment.Moment]
  groupOfCondition?: number
  selectedDay?: string
  weekNumberInMonth?: number
  groupOfWeekDays?: string[]
  groupOfDays?: string[]
  customDates?: string
}

type AlertMessage = {
  customer?: string
  bookingDetail?: string
  combination?: string
}

export const Head = () => <SEO title="予約一括作成" />

const IndexPage = () => {
  const [selectedDates, setSelectedDates] = useState<Key[]>([])
  const [selectedVariants, setSelectedVariants] = useState<
    CustomVariantAvailable[]
  >([])
  const [times, setTimes] = useState<{ start: string; end: string }>(null)
  const [customerInfo, setCustomerInfo] = useState<Customer>(null)
  const [bookingDetail, setBookingDetail] = useState<BookingDetail>(null)
  const [bookingUsageScheduleDetail, setBookingUsageScheduleDetail] =
    useState<BookingUsageScheduleDetail>({})
  const [confirmModal, setConfirmModal] = useState<boolean>(false)
  const [alert, setAlert] = useState<AlertMessage>({})
  const [form] = Form.useForm<DatetimeSearchForm>()

  // Bookings Bulk error useState
  const [showBulkErrorAlert, setShowBulkErrorAlert] = useState(false)
  const [bulkErrorDescriptions, setBulkErrorDescriptions] = useState<string[]>(
    []
  )

  const [getBookings, { data, loading, error }] =
    useProductWholeBookingLazyQuery({})

  const onSearch = async (values: DatetimeSearchForm) => {
    // 既存の選択を初期化
    setSelectedDates([])
    setSelectedVariants([])
    const {
      startDateToEndDate,
      timezone,
      groupOfCondition,
      weekNumberInMonth,
      groupOfWeekDays,
      selectedDay,
      groupOfDays,
      customDates,
    } = values

    const startDate = values.startDateToEndDate[0].clone()
    const dates: string[] = [startDate.format('YYYY-MM-DDT00:00:00')]
    while (startDateToEndDate[1].diff(startDate, 'days') > 0) {
      startDate.add(1, 'days')
      dates.push(startDate.format('YYYY-MM-DDT00:00:00'))
    }

    setTimes({
      start: timezone[0].format('HH:mm'),
      end: timezone[1].format('HH:mm'),
    })

    let submitDates = [...dates]
    if (typeof groupOfCondition !== 'undefined') {
      if (groupOfCondition === 1 && typeof selectedDay !== 'undefined') {
        const matchDays = getGivenDayMatch({
          dates: dates,
          day: selectedDay,
        })
        submitDates = matchDays
      } else if (
        groupOfCondition === 2 &&
        typeof weekNumberInMonth !== 'undefined'
      ) {
        const matchDays = getWeekInMonthAndGroupOfDaysMatch({
          dates: dates,
          weekNumberInMonth: weekNumberInMonth,
          groupOfDays: groupOfWeekDays,
        })
        submitDates = matchDays
      } else if (groupOfCondition === 3 && typeof groupOfDays !== 'undefined') {
        const matchDays = getGroupOfDaysMatch({
          dates: dates,
          groupOfDays: groupOfDays,
        })
        submitDates = matchDays
      } else if (groupOfCondition === 4 && typeof customDates !== 'undefined') {
        if (customDates.length > 10 && customDates.search(',') === -1) {
          showMessage('error', '日付の書式が正しくありません｡')
          return
        } else {
          const splitCustomDates = customDates?.split(',')
          splitCustomDates?.forEach((date: string) => {
            if (!date.match(dateRegexFormat)) {
              showMessage('error', '日付の書式が正しくありません｡')
              return
            }
          })
          const matchDates = getAnyMatchDates({
            dates: dates,
            customDates:
              splitCustomDates?.length > 0 ? splitCustomDates : [customDates],
          })
          submitDates = matchDates
        }
      }
    }

    try {
      await getBookings({
        variables: {
          input: {
            dates: submitDates,
            start: timezone[0].format('HH:mm'),
            end: timezone[1].format('HH:mm'),
          },
        },
      })
    } catch (e) {
      showMessage('error', '検索条件が一致していません。')
    }
  }

  // ここから CustomerSearchCard 用関数
  const [searchCustomers, { data: customersData, loading: customersLoading }] =
    useCustomersLazyQuery()

  const handleSearchCustomer = (values: FilterFormElement) => {
    const inputs: CustomerQueryInput[] = []
    Object.entries(values).forEach(([key, value]) => {
      if (value) {
        inputs.push({
          field: key,
          value: value?.replace(/\s{2,}/g, ' ').trim(),
          condition: CustomerConditionType.Contains,
          type: CustomerQueryType.And,
        })
      }
    })
    searchCustomers({ variables: { input: inputs } })
  }

  const onSelectCustomer = (value: Customer) => {
    setCustomerInfo(value || null)
  }
  // ここまで CustomerSearchCard 用関数

  const [CreateBatch, { error: batchError }] = useOrderCreateBatchMutation({})

  // Mutation 実行前のバリデーション
  const handleAlert = (): AlertMessage => {
    const messages: AlertMessage = {}
    if (!customerInfo) messages.customer = 'お客様の選択は必須です'
    if (!bookingDetail)
      messages.bookingDetail = '予約詳細情報を入力してください'
    if (!selectedVariants.length || !selectedDates.length)
      messages.combination = '予約する日付と施設の組み合わせを選択してください'
    return messages
  }

  const handleValidate = () => {
    const messages = handleAlert()
    setAlert(messages)
    if (Object.keys(messages).length) return

    const pickBookings = selectedDates?.map((date) =>
      data?.productWholeBooking?.edges?.find(({ wholeBooking }) => {
        return wholeBooking?.bookingDate === date
      })
    )
    const validateResult = selectedVariants?.reduce((result, { variantId }) => {
      let validate = null
      pickBookings?.forEach(({ wholeBooking }) => {
        wholeBooking?.bookingVariants?.edges?.forEach(({ variant }) => {
          if (variant.variantId === variantId) {
            // TODO: 必要であればこの情報を使ってエラーメッセージを出力
            if (!variant.isAvailable)
              validate = {
                date: wholeBooking.bookingDate,
                variant: variant.variantName,
              }
          }
        })
      })
      if (validate) result.push(validate)
      return result
    }, [])
    if (validateResult.length > 0) {
      showMessage(
        'error',
        '予約のできない組み合わせがあります。もう一度選び直してください。'
      )
      return
    }
    // バリデーション通過後に予約作成確認Modalオープン
    setConfirmModal(true)
  }

  // Mutation 実行
  const onBatch = async () => {
    try {
      const input: OrderCreateBatchInput = {
        // TODO: ProductWholeBooking Query の方を修正し次第 .map 以降を外す
        dates: selectedDates.map((date: string) => date.substring(0, 10)),
        period: times,
        productVariants: selectedVariants.map((variant) => ({
          variantId: variant.variantId,
          quantity: 1,
        })),
        customerId: customerInfo.id,
        numberOfPeople: bookingDetail.numberOfPeople,
        contactOfRepresentative: {
          name: bookingDetail.contactOfRepresentative?.name,
          email: bookingDetail.contactOfRepresentative?.email,
          phone: bookingDetail.contactOfRepresentative?.phone,
        },
        purpose: bookingDetail.purpose,
        entryFee: bookingDetail.entryFee,
        saleOfGoods: bookingDetail.saleOfGoods,
        calendar: {
          approval: bookingUsageScheduleDetail?.approval,
          organizer: bookingUsageScheduleDetail?.organizer,
          eventName: bookingUsageScheduleDetail?.eventName,
          publishableDate: bookingUsageScheduleDetail?.publishableDate,
        },
      }
      const { data } = await CreateBatch({ variables: { input: input } })
      const bulkUserErrors = data?.orderCreateBatch?.orderUserErrors
      if (bulkUserErrors.length > 0) {
        const bulkErrorDescriptions = bookingsErrorHandling(bulkUserErrors)
        if (
          bulkErrorDescriptions[0] === 'メール送信時にエラーが発生しました。'
        ) {
          showMessage(
            'success',
            'メール送信時にエラーが発生しましたが、予約を作成しました。'
          )
          await navigate(routes.bookings)
        } else {
          setShowBulkErrorAlert(true)
          showMessage('error', '予約作成時にエラーが発生しました。')
          setBulkErrorDescriptions(bulkErrorDescriptions)
        }
      } else {
        setShowBulkErrorAlert(false)
        showMessage('success', '予約を作成しました。')
        await navigate(routes.bookings)
      }
    } catch (e) {
      setShowBulkErrorAlert(true)
      showMessage(
        'error',
        <>
          <div>予約作成時にエラーが発生しました</div>
          <div>{e.message}</div>
        </>
      )
    }
  }

  const onCloseAlert = () => {
    setShowBulkErrorAlert(false)
  }

  if (batchError) return <div>{batchError.message}</div>
  if (error) {
    return Error.render(error)
  }

  return (
    <>
      <div className={clsx('flex', 'items-start', 'justify-between')}>
        <Title level={1}>予約一括作成</Title>
        <Anchor />
      </div>
      <Row gutter={3}>
        <Col span={8}>
          {showBulkErrorAlert && (
            <ErrorAlert
              alertDescription={bulkErrorDescriptions}
              onCloseAlert={onCloseAlert}
            />
          )}
          <Form form={form} component="form" onFinish={onSearch}>
            <Card style={{ marginBottom: '10px', marginRight: '4px' }}>
              <Col span={24} style={{ marginBottom: '8px' }}>
                <Info
                  style={{ marginBottom: '1rem' }}
                  title={`開始日は終了日と同じかそれより前である必要があります。 例：2022-01-01〜2022-01-01/2022-01-01〜22-01-25。`}
                >
                  <Title level={5} style={{ marginBottom: 0 }}>
                    日付の選択
                  </Title>
                </Info>
              </Col>
              <Form.Item
                name="startDateToEndDate"
                validateTrigger="onChange"
                rules={[{ required: true, message: '開始日の入力は必須です' }]}
              >
                <DateRangePicker
                  picker="date"
                  disabledDate={(current) => {
                    return current && current < moment().subtract(1, 'days')
                  }}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Col span={24} style={{ marginBottom: '8px' }}>
                <Info
                  title={`開始時刻は終了時刻より前である必要があります。例：09:00 ~ 15:00。`}
                >
                  <Title level={5} style={{ marginBottom: '0px' }}>
                    時間帯の選択
                  </Title>
                </Info>
              </Col>
              <Form.Item
                name="timezone"
                rules={[{ required: true, message: '時間帯の入力は必須です' }]}
              >
                <TimeRangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Card>

            <SearchConditionsCard title={'検索条件'} form={form} />
            <Card style={{ marginBottom: '10px', marginRight: '5px' }}>
              <Button
                type={'primary'}
                htmlType="submit"
                style={{ width: '100%' }}
                loading={loading}
              >
                検索
              </Button>
            </Card>
          </Form>
        </Col>
        <Col span={8} style={{ paddingRight: '5px', paddingLeft: '5px' }}>
          <CustomerSearchCard
            title={'お客様を選択'}
            data={customersData}
            loading={customersLoading}
            onSearch={handleSearchCustomer}
            onSelect={onSelectCustomer}
          />
          {alert?.customer && (
            <Alert showIcon message={alert.customer} type="error" />
          )}
        </Col>

        <Col span={8} style={{ paddingLeft: '5px' }}>
          <BookingInfoCard
            customer={{
              id: customerInfo?.id,
              firstName: customerInfo?.firstName,
              lastName: customerInfo?.lastName,
              customerType: customerInfo?.type,
              email: customerInfo?.email,
              phone: customerInfo?.phone,
              corporationName: customerInfo?.corporationName,
            }}
            bookingDetail={bookingDetail && bookingDetail}
            onDetailHold={setBookingDetail}
            usageScheduleDetails={{
              approval: bookingUsageScheduleDetail.approval,
              organizer: bookingUsageScheduleDetail.organizer,
              eventName: bookingUsageScheduleDetail.eventName,
              publishableDate: bookingUsageScheduleDetail.publishableDate,
            }}
            onUsageScheduleDetailsHold={setBookingUsageScheduleDetail}
            editable={true}
          />
          {/* TODO: API側機能未実装のため一時的に除外 */}
          {/* <ReservationCard title={'予約詳細'} />
            <TagsCard /> */}
          {alert?.bookingDetail && (
            <Alert showIcon message={alert.bookingDetail} type="error" />
          )}
        </Col>
      </Row>
      {data?.productWholeBooking && (
        <Card
          title={<Title level={3}>検索結果</Title>}
          extra={<Button onClick={handleValidate}>予約を一括作成する</Button>}
        >
          {alert?.combination && (
            <Alert showIcon message={alert.combination} type="error" />
          )}
          <BulkTable
            data={data}
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
            selectedVariants={selectedVariants}
            setSelectedVariants={setSelectedVariants}
          />
          <Modal
            visible={confirmModal}
            onCancel={() => setConfirmModal(false)}
            onOk={() => {
              onBatch()
              setConfirmModal(false)
            }}
            title={<div>以下の組み合わせで予約を作成します</div>}
            okText={'一括作成'}
            cancelText={'キャンセル'}
            width={600}
            bodyStyle={{ height: '500px', overflowY: 'scroll' }}
          >
            <div>
              <Title level={5}>予約時間帯</Title>
              <div>
                {times.start}～{times.end}
              </div>
            </div>
            <Row gutter={2}>
              <Col span={12}>
                <List
                  size="small"
                  header={<Title level={5}>予約日</Title>}
                  dataSource={selectedDates}
                  renderItem={(item) => (
                    <List.Item>{String(item).substring(0, 10)}</List.Item>
                  )}
                />
              </Col>
              <Col span={12}>
                <List
                  size="small"
                  header={<Title level={5}>予約施設</Title>}
                  dataSource={selectedVariants}
                  renderItem={(item) => (
                    <List.Item>{item.variantName}</List.Item>
                  )}
                />
              </Col>
            </Row>
          </Modal>
        </Card>
      )}
    </>
  )
}

export default IndexPage
